import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/ui/data-table';
import { Modal } from '@/components/ui/modal';
import { ActionButton } from '@/components/ui/action-button';
import { supabase } from '@/lib/supabase';
import type { Region } from '@/types';

export default function Regions() {
  const [regions, setRegions] = useState<Region[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const [newRegionName, setNewRegionName] = useState('');

  useEffect(() => {
    fetchRegions();
  }, []);

  const fetchRegions = async () => {
    try {
      const { data, error } = await supabase
        .from('sms_regions')
        .select('*')
        .order('name');

      if (error) throw error;
      setRegions(data);
    } catch (error) {
      console.error('Error fetching regions:', error);
      toast.error('Failed to fetch regions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (selectedRegion) {
        // Update existing region
        const { error } = await supabase
          .from('sms_regions')
          .update({ name: newRegionName })
          .eq('id', selectedRegion.id);

        if (error) throw error;
        toast.success('Region updated successfully');
      } else {
        // Create new region
        const { error } = await supabase
          .from('sms_regions')
          .insert({ name: newRegionName });

        if (error) throw error;
        toast.success('Region created successfully');
      }

      setModalOpen(false);
      fetchRegions();
    } catch (error) {
      console.error('Error saving region:', error);
      toast.error('Failed to save region');
    }
  };

  const handleDelete = async () => {
    if (!selectedRegion) return;

    try {
      const { error } = await supabase
        .from('sms_regions')
        .delete()
        .eq('id', selectedRegion.id);

      if (error) throw error;

      toast.success('Region deleted successfully');
      setDeleteDialogOpen(false);
      fetchRegions();
    } catch (error) {
      console.error('Error deleting region:', error);
      toast.error('Failed to delete region');
    }
  };

  const columns = [
    { header: 'Name', accessorKey: 'name' },
    {
      header: 'Actions',
      accessorKey: 'actions',
      cell: ({ row }: any) => (
        <div className="flex gap-2">
          <ActionButton
            icon={Pencil}
            variant="edit"
            onClick={() => {
              setSelectedRegion(row.original);
              setNewRegionName(row.original.name);
              setModalOpen(true);
            }}
          />
          <ActionButton
            icon={Trash2}
            variant="delete"
            onClick={() => {
              setSelectedRegion(row.original);
              setDeleteDialogOpen(true);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="container mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Regions</h1>
        <Button
          onClick={() => {
            setSelectedRegion(null);
            setNewRegionName('');
            setModalOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Region
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={regions}
        isLoading={isLoading}
      />

      <Modal
        title={selectedRegion ? 'Edit Region' : 'Add Region'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleSubmit}
      >
        <div>
          <label className="text-sm font-medium mb-1 block">Name</label>
          <Input
            value={newRegionName}
            onChange={(e) => setNewRegionName(e.target.value)}
          />
        </div>
      </Modal>

      <Modal
        title="Delete Region"
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        confirmText="Delete"
        confirmVariant="destructive"
      >
        <p>Are you sure you want to delete this region? This action cannot be undone.</p>
      </Modal>
    </div>
  );
}